import React from "react";
import { Avatar, TablePagination, Typography } from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { patientSearchResult } from "@finpay-development/shared-platform/lib/esm/admissions-advisor/models/patient";

import classes from "./global-search.module.css";
import { getFullName, getInitials, getPatientMeta } from "./global-search-utils";
import { useClientNamesById } from "./global-search-client-filter";

interface GlobalSearchPatientListProps {
  patients: patientSearchResult[];
  totalCount: number;
  selectedId: string | null;
  page: number;
  rowsPerPage: number;
  onSelect: (patient: patientSearchResult) => void;
  onPageChange: (event: unknown, newPage: number) => void;
  onRowsPerPageChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

const GlobalSearchPatientList = (props: GlobalSearchPatientListProps) => {
  const {
    patients,
    totalCount,
    selectedId,
    page,
    rowsPerPage,
    onSelect,
    onPageChange,
    onRowsPerPageChange,
  } = props;
  const clientNamesById = useClientNamesById();

  return (
    <>
      <div className={classes.sectionLabel}>Patients ({totalCount})</div>
      <div className={classes.scroll} role="listbox" aria-label="Patient results">
        {patients.map((patient) => {
          const isSelected = patient._id === selectedId;
          const meta = getPatientMeta(patient._source, clientNamesById);
          return (
            <div
              key={patient._id}
              role="option"
              aria-selected={isSelected}
              tabIndex={0}
              className={`${classes.patientItem} ${isSelected ? classes.patientItemSelected : ""}`}
              onClick={() => onSelect(patient)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  onSelect(patient);
                }
              }}
              test-id={`global-search-patient-${patient._id}`}
            >
              <Avatar className={classes.avatar}>{getInitials(patient._source)}</Avatar>
              <div className={classes.patientText}>
                <Typography variant="body2" className={classes.patientName}>
                  {getFullName(patient._source)}
                </Typography>
                <div className={classes.patientMeta} title={meta}>
                  {meta}
                </div>
              </div>
              <ChevronRightIcon fontSize="small" className={classes.chevron} />
            </div>
          );
        })}
      </div>
      <TablePagination
        component="div"
        count={totalCount}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={onPageChange}
        onRowsPerPageChange={onRowsPerPageChange}
        rowsPerPageOptions={[5, 10, 25]}
        size="small"
      />
    </>
  );
};

export default GlobalSearchPatientList;
